import React, { useSyncExternalStore } from 'react';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell, StatefulPagination } from '../ui';
import type { PaginationMeta } from '../../hooks/usePagination';

export interface ColumnDef<T> {
  key: string;
  header: string;
  sortable?: boolean;
  render: (item: T) => React.ReactNode;
}

interface PaginatedTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  pagination?: PaginationMeta;
  isLoading?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  onPageChange?: (page: number) => void;
  onRowClick?: (item: T) => void;
  getRowKey: (item: T) => string;
  emptyMessage?: string;
}

const SM_QUERY = '(min-width: 640px)';

function subscribeToMedia(callback: () => void) {
  const mql = window.matchMedia(SM_QUERY);
  mql.addEventListener('change', callback);
  return () => mql.removeEventListener('change', callback);
}

function getIsSmScreen() {
  return window.matchMedia(SM_QUERY).matches;
}

function getIsSmScreenServer() {
  return true;
}

function PaginatedTable<T>({
  columns,
  data,
  pagination,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  onPageChange,
  onRowClick,
  getRowKey,
  emptyMessage = 'No results found.'
}: PaginatedTableProps<T>) {
  const isSmScreen = useSyncExternalStore(subscribeToMedia, getIsSmScreen, getIsSmScreenServer);

  if (isLoading) {
    return <p>Loading...</p>;
  }

  if (data.length === 0) {
    return <p>{emptyMessage}</p>;
  }

  const showingStart = pagination ? (pagination.page - 1) * pagination.pageSize + 1 : 1;
  const showingEnd = pagination ? Math.min(pagination.page * pagination.pageSize, pagination.total) : data.length;
  const totalItems = pagination?.total ?? data.length;

  return (
    <div>
      {isSmScreen ? (
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableHeader
                  key={col.key}
                  onClick={col.sortable && onSort ? () => onSort(col.key) : undefined}
                  onKeyDown={
                    col.sortable && onSort
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSort(col.key);
                          }
                        }
                      : undefined
                  }
                  tabIndex={col.sortable && onSort ? 0 : undefined}
                  role={col.sortable && onSort ? 'button' : undefined}
                  className={col.sortable && onSort ? 'cursor-pointer select-none' : undefined}
                >
                  {col.header}
                  {sortBy === col.key && <span className="ml-1">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</span>}
                </TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow
                key={getRowKey(item)}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(item);
                        }
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'link' : undefined}
                className={onRowClick ? 'cursor-pointer' : undefined}
              >
                {columns.map((col) => (
                  <TableCell key={col.key}>{col.render(item)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="space-y-3">
          {data.map((item) => (
            <div
              key={getRowKey(item)}
              className={`rounded-lg border border-(--color-border-default) p-4 space-y-2${onRowClick ? ' cursor-pointer hover:bg-(--color-surface-hover)' : ''}`}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(item);
                      }
                    }
                  : undefined
              }
              tabIndex={onRowClick ? 0 : undefined}
              role={onRowClick ? 'link' : undefined}
            >
              {columns.map((col) => (
                <div key={col.key} className="flex justify-between gap-4">
                  <span className="text-sm font-medium text-(--color-text-muted)">{col.header}</span>
                  <span className="text-sm text-(--color-text-primary) text-right">{col.render(item)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-4">
        <p className="text-sm text-(--color-text-muted)">
          Showing {showingStart}&ndash;{showingEnd} of {totalItems}
        </p>
        {pagination && pagination.totalPages > 1 && onPageChange && (
          <StatefulPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            maxSlots={7}
            onPageChange={onPageChange}
          />
        )}
      </div>
    </div>
  );
}

export default PaginatedTable;
